import { useEffect, useState } from "react";
import type { TaskItem } from "@edge/shared";
import { WriteConfirm } from "./WriteConfirm";

type CrmPriority = "Highest" | "High" | "Normal" | "Low" | "Lowest";
type CrmStatus =
  | "Not Started"
  | "In Progress"
  | "Completed"
  | "Waiting on someone else"
  | "Deferred";

const STATUS_OPTIONS: CrmStatus[] = [
  "Not Started",
  "In Progress",
  "Completed",
  "Waiting on someone else",
  "Deferred",
];

export interface CrmTaskEditDialogProps {
  task: TaskItem;
  /** Throws on failure — dialog stays open with error. */
  onSave: (
    taskId: string,
    fields: {
      subject?: string;
      due_date?: string | null;
      priority?: CrmPriority;
      status?: CrmStatus;
      description?: string;
    },
  ) => Promise<void>;
  onCancel: () => void;
}

/** Map TaskItem.priority (cockpit-normalized) back to a Zoho picklist value. */
function priorityToZoho(p: TaskItem["priority"]): CrmPriority {
  if (p === "high") return "High";
  if (p === "low") return "Low";
  return "Normal";
}

/** Normalize the raw Zoho status onto one of the picklist values. */
function statusToZoho(s: TaskItem["status"]): CrmStatus {
  return s && (STATUS_OPTIONS as string[]).includes(s)
    ? (s as CrmStatus)
    : "Not Started";
}

/** Timezone-safe date label from an ISO yyyy-mm-dd (no Date() parsing). */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDate(iso: string | null): string {
  if (!iso) return "(none)";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** Truncate a long body for the diff display. */
function trunc(s: string, n = 80): string {
  const t = s.trim();
  if (!t) return "(empty)";
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

interface Change {
  label: string;
  from: string;
  to: string;
}

/**
 * Edit a Zoho CRM task in place: subject, due date, priority, status, and
 * description. The linked deal is read-only here (re-parenting a task is rarely
 * the intent and adds a Zoho lookup hop — leave it for a future cross-deal-move
 * action). Two-phase: edit form → an explicit before→after review (the shared
 * WriteConfirm gate, same as reschedule) → write. Only changed fields are sent.
 * Boundary rules match the WriteConfirm family: Escape or CANCEL only, backdrop
 * doesn't dismiss.
 */
export function CrmTaskEditDialog({
  task,
  onSave,
  onCancel,
}: CrmTaskEditDialogProps) {
  const initialStatus = statusToZoho(task.status);
  const initialPriority = priorityToZoho(task.priority);

  const [subject, setSubject] = useState(task.subject);
  const [dueDate, setDueDate] = useState(task.dueDate ?? "");
  const [priority, setPriority] = useState<CrmPriority>(initialPriority);
  const [status, setStatus] = useState<CrmStatus>(initialStatus);
  const [description, setDescription] = useState(task.description ?? "");
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Escape closes the EDIT phase (the review phase is handled by WriteConfirm).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !reviewing) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, reviewing]);

  // Diff the form against the task. Returns both the human-readable change list
  // (for the review gate) and the minimal PATCH payload (changed fields only).
  const subjTrim = subject.trim();
  const newDue = dueDate || null;
  const origDue = task.dueDate ?? null;
  const origDesc = (task.description ?? "").trim();
  const newDesc = description.trim();

  const changes: Change[] = [];
  const fields: {
    subject?: string;
    due_date?: string | null;
    priority?: CrmPriority;
    status?: CrmStatus;
    description?: string;
  } = {};

  if (subjTrim && subjTrim !== task.subject) {
    changes.push({ label: "Subject", from: task.subject, to: subjTrim });
    fields.subject = subjTrim;
  }
  if (newDue !== origDue) {
    changes.push({ label: "Due date", from: fmtDate(origDue), to: newDue ? fmtDate(newDue) : "(cleared)" });
    fields.due_date = newDue;
  }
  if (priority !== initialPriority) {
    changes.push({ label: "Priority", from: initialPriority, to: priority });
    fields.priority = priority;
  }
  if (status !== initialStatus) {
    changes.push({ label: "Status", from: initialStatus, to: status });
    fields.status = status;
  }
  if (newDesc !== origDesc) {
    changes.push({ label: "Description", from: trunc(origDesc), to: newDesc ? trunc(newDesc) : "(cleared)" });
    fields.description = newDesc;
  }

  const hasChanges = changes.length > 0;

  const handleReview = () => {
    if (!subjTrim) {
      setError("subject_required");
      return;
    }
    if (!hasChanges) {
      // Nothing to do — close as if cancelled.
      onCancel();
      return;
    }
    setError(null);
    setReviewing(true);
  };

  if (reviewing) {
    return (
      <WriteConfirm
        title="Save changes to Zoho CRM task?"
        body={<div className="wc-task-subject">{subjTrim}</div>}
        changes={changes}
        confirmLabel="SAVE TO ZOHO"
        onConfirm={async () => {
          await onSave(task.id, fields);
        }}
        onCancel={() => setReviewing(false)}
      />
    );
  }

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card crm-create-card">
        <div className="write-confirm-title">Edit Zoho CRM task</div>
        <div className="crm-create-body">
          <label className="crm-create-field">
            <span className="crm-create-label">Subject</span>
            <input
              className="crm-create-input"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </label>
          <div className="crm-create-row">
            <label className="crm-create-field crm-create-field-half">
              <span className="crm-create-label">Due date</span>
              <input
                className="crm-create-input"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>
            <label className="crm-create-field crm-create-field-half">
              <span className="crm-create-label">Priority</span>
              <select
                className="crm-create-input"
                value={priority}
                onChange={(e) => setPriority(e.target.value as CrmPriority)}
              >
                <option value="Highest">Highest</option>
                <option value="High">High</option>
                <option value="Normal">Normal</option>
                <option value="Low">Low</option>
                <option value="Lowest">Lowest</option>
              </select>
            </label>
          </div>
          <label className="crm-create-field">
            <span className="crm-create-label">Status</span>
            <select
              className="crm-create-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as CrmStatus)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="crm-create-field">
            <span className="crm-create-label">Description</span>
            <textarea
              className="crm-create-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="No description"
            />
          </label>
          {task.dealName && (
            <div className="crm-create-field crm-create-deal-readonly">
              <span className="crm-create-label">Linked deal</span>
              <span className="crm-create-deal-value">{task.dealName}</span>
            </div>
          )}
        </div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button className="write-confirm-cancel" onClick={onCancel}>
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleReview}
            disabled={!subjTrim || !hasChanges}
            title={
              !subjTrim
                ? "Subject is required"
                : !hasChanges
                  ? "No changes yet"
                  : "Review the changes before writing to Zoho"
            }
          >
            REVIEW CHANGES →
          </button>
        </div>
      </div>
    </div>
  );
}
